import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Client } from '@stomp/stompjs';
import { appConfig } from '../../config';
import { getSharedConstants } from '../../store/baseQuery';

interface Subscription {
  unsubscribe: () => void;
}

interface SubscriptionEntry<T> {
  destination: string;
  callbacks: Array<(_data: T) => void>;
  subscription?: Subscription;
}

interface StompContextProps {
  client: Client | null;
  connected: boolean;
  connect: (_token?: string) => void;
  reconnect: (_token: string) => void;
  emitMessage: <T>(_destination: string, _data: T) => boolean;
  addSubscription: <T>(
    _destination: string,
    _callback: (_data: T) => void
  ) => void;
  removeSubscription: <T>(
    _destination: string,
    _callback: (_data: T) => void
  ) => void;
  removeAllSubscriptionsOnDestination: (_destination: string) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const StompContext = createContext<StompContextProps>({
  client: null,
  connected: false,
  connect: () => {},
  reconnect: () => {},
  addSubscription: () => {},
  removeSubscription: () => {},
  removeAllSubscriptionsOnDestination: () => {},
  emitMessage: () => false,
});

export const StompProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const clientRef = useRef<Client | null>(null);
  const tokenRef = useRef<string | null>(null);
  const connectingRef = useRef(false);
  const [connected, setConnected] = useState(false);
  const subscriptionsRef = useRef<{
    [destination: string]: SubscriptionEntry<unknown>;
  }>({});

  const clearSubscriptionRefs = useCallback(() => {
    Object.keys(subscriptionsRef.current).forEach((key) => {
      subscriptionsRef.current[key].subscription = undefined;
    });
  }, []);

  const connect = useCallback(
    async (token?: string) => {
      const { APP_TOKEN_KEY, X_AUTH_HEADER } = getSharedConstants();

      // Check both React state and ref for active connection
      if (connected || connectingRef.current || clientRef.current?.active) {
        if (token === tokenRef.current) {
          // Duplicate connections
          return;
        }
        await clientRef?.current?.deactivate();
        clientRef.current = null;
      }

      const storedToken = localStorage.getItem(APP_TOKEN_KEY) || token;

      if (!storedToken) {
        return;
      }

      connectingRef.current = true;
      tokenRef.current = storedToken;

      const headers: { [key: string]: string } = storedToken
        ? { [X_AUTH_HEADER]: storedToken }
        : {};

      const client = new Client({
        brokerURL: appConfig.wsConfig.wsHost,
        connectHeaders: headers,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        reconnectDelay: 5000,
        onConnect: () => {
          console.log('STOMP connected');
          connectingRef.current = false;
          setConnected(true);

          Object.keys(subscriptionsRef.current).forEach((destination) => {
            if (!subscriptionsRef.current[destination].subscription) {
              subscriptionsRef.current[destination].subscription =
                client.subscribe(destination, (message) => {
                  const data = JSON.parse(message.body);
                  subscriptionsRef.current[destination].callbacks.forEach(
                    (callback) => callback(data)
                  );
                });
            }
          });
        },
        onStompError: (errorFrame) => {
          console.error('STOMP error:', errorFrame);
          connectingRef.current = false;
        },
        onDisconnect: () => {
          console.log('STOMP disconnected');
          connectingRef.current = false;
          setConnected(false);
          clearSubscriptionRefs();
        },
        onWebSocketError: (error) => {
          console.error('WebSocket error:', error);
          connectingRef.current = false;
        },
        onWebSocketClose: () => {
          console.log('WebSocket closed');
          connectingRef.current = false;
          setConnected(false);
          clearSubscriptionRefs();
        },
      });

      client.activate();
      clientRef.current = client;
    },
    [connected, clearSubscriptionRefs]
  );

  const addSubscription = useCallback(
    <T = unknown,>(destination: string, callback: (_data: T) => void) => {
      const entry = subscriptionsRef.current[destination];

      if (!entry) {
        subscriptionsRef.current[destination] = {
          destination,
          callbacks: [callback as (_data: unknown) => void],
        };

        // Subscribe immediately if the socket is connected; otherwise, onConnect will handle it.
        if (clientRef.current?.connected) {
          subscriptionsRef.current[destination].subscription =
            clientRef.current.subscribe(destination, (message) => {
              const data = JSON.parse(message.body);
              subscriptionsRef.current[destination].callbacks.forEach((cb) =>
                cb(data)
              );
            });
        }
      } else if (
        !entry.callbacks.includes(callback as (_data: unknown) => void)
      ) {
        entry.callbacks.push(callback as (_data: unknown) => void);
      }
    },
    []
  );

  const removeSubscription = useCallback(
    <T = unknown,>(destination: string, callback: (_data: T) => void) => {
      const entry = subscriptionsRef.current[destination];
      if (entry) {
        entry.callbacks = entry.callbacks.filter(
          (cb) => cb !== (callback as (_data: unknown) => void)
        );

        if (entry.callbacks.length === 0) {
          entry.subscription?.unsubscribe();
          delete subscriptionsRef.current[destination];
        }
      }
    },
    []
  );

  const removeAllSubscriptionsOnDestination = useCallback(
    (destination: string) => {
      const entry = subscriptionsRef.current[destination];
      if (entry) {
        entry.subscription?.unsubscribe();
        delete subscriptionsRef.current[destination];
      }
      if (clientRef.current) {
        clientRef.current.unsubscribe(destination);
      }
    },
    []
  );

  const clearSubscriptions = useCallback(() => {
    Object.values(subscriptionsRef.current).forEach(({ subscription }) => {
      subscription?.unsubscribe();
    });
    clearSubscriptionRefs();
  }, [clearSubscriptionRefs]);

  const reconnect = useCallback(
    async (token: string) => {
      if (
        tokenRef.current === token &&
        (clientRef.current?.connected || connectingRef.current)
      ) {
        return;
      }

      try {
        if (clientRef.current) {
          clearSubscriptions();
          await clientRef.current.deactivate();
          clientRef.current = null;
        }
      } catch (error) {
        console.error('[WebSocket] Error during deactivation:', error);
      }

      connectingRef.current = false;
      setConnected(false);
      tokenRef.current = token;

      setTimeout(async () => {
        await connect(token);
      }, 100);
    },
    [clearSubscriptions, connect]
  );

  const emitMessage = useCallback(
    <T = unknown,>(destination: string, data: T) => {
      if (clientRef.current?.connected) {
        try {
          clientRef.current.publish({
            destination,
            body: JSON.stringify(data),
          });
          return true;
        } catch {
          return false;
        }
      }
      return false;
    },
    []
  );

  useEffect(() => {
    const { APP_TOKEN_KEY } = getSharedConstants();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (clientRef.current && !clientRef.current.connected) {
          const token = tokenRef.current || localStorage.getItem(APP_TOKEN_KEY);
          if (token) {
            reconnect(token);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [reconnect]);

  return (
    <StompContext.Provider
      value={{
        client: clientRef.current,
        connect,
        reconnect,
        connected,
        addSubscription,
        removeSubscription,
        removeAllSubscriptionsOnDestination,
        emitMessage,
      }}
    >
      {children}
    </StompContext.Provider>
  );
};

export default StompProvider;
